import axios from 'axios'
import { API_URL } from 'services/config'
import PolicyEntity from 'models/PolicyEntity'
import PoliciesEntity from 'models/PoliciesEntity'
import PolicyBank from 'models/PolicyBank'
import PolicyFinancialInstitution from 'models/PolicyFinancialInstitution'
import CbuAccountType from 'models/CbuAccountType'
import PolicyAmendment from 'models/PolicyAmendment'
import PolicyAmendmentDetail from 'models/PolicyAmendmentDetail'
import PolicyAmendmentPetitions from 'models/PolicyAmendmentPetitions'
import PolicyAmendmentPetitionDetail from 'models/PolicyAmendmentPetitionDetail'
import Paginator from 'models/Paginator'
import VehicleCoverage from 'models/VehicleCoverage'
import PolicyEmitted from 'models/PolicyEmitted'
import {
    EMIT_STATUS_WAITING,
    EMIT_STATUS_NEEDS_REQUOTE,
    EMIT_STATUS_SUCCESS,
    EMIT_STATUS_NEEDS_CONFIRMATION,
} from 'util/constants'
import i18next from 'i18next'
import AccountIcon from 'mdi-react/AccountIcon'
import AccountLockIcon from 'mdi-react/AccountLockIcon'
import CarIcon from 'mdi-react/CarIcon'
import CreditCardIcon from 'mdi-react/CreditCardIcon'
import {
    QUOTES_CREATION_CAR,
} from 'routing/routes'
import FormStepHeaderOption from 'models/FormStepHeaderOption'
import CommonDataStore from 'stores/CommonDataStore'
import BrokerDto from 'dto/BrokerDto'

const QUOTE_VEHICLE_LEGACY_EMIT = `${QUOTES_CREATION_CAR}/emitir-poliza`
const QUOTE_VEHICLE_LEGACY_EMIT_CLIENT = `${QUOTE_VEHICLE_LEGACY_EMIT}/cliente`
const QUOTE_VEHICLE_LEGACY_EMIT_INSURED = `${QUOTE_VEHICLE_LEGACY_EMIT}/asegurado`
const QUOTE_VEHICLE_LEGACY_EMIT_ASSET = `${QUOTE_VEHICLE_LEGACY_EMIT}/auto`
const QUOTE_VEHICLE_LEGACY_EMIT_PAYMENT = `${QUOTE_VEHICLE_LEGACY_EMIT}/pago`

const policyStepsHeaderJson = [
    {
        name: i18next.t('quoteCreation:clientData'),
        icon: AccountIcon,
        route: QUOTE_VEHICLE_LEGACY_EMIT_CLIENT,
    },
    {
        name: i18next.t('quoteCreation:insuredData'),
        icon: AccountLockIcon,
        route: QUOTE_VEHICLE_LEGACY_EMIT_INSURED,
    },
    {
        name: i18next.t('quoteCreation:carData'),
        icon: CarIcon,
        route: QUOTE_VEHICLE_LEGACY_EMIT_ASSET,
    },
    {
        name: i18next.t('quoteCreation:paymentData'),
        icon: CreditCardIcon,
        route: QUOTE_VEHICLE_LEGACY_EMIT_PAYMENT,
    },
]

class PolicyService {
    createPolicy = (params) =>
        axios.post(`${API_URL}/emissions`, params).then((response) => response)

    createInsured = (policyData) =>
        axios.post(`${API_URL}/insured`, policyData).then(({ data }) => data.data)

    createAsset = (carAssetData) =>
        axios.post(`${API_URL}/assets/car`, carAssetData).then((response) => response)

    createDynamicAsset = (assetData, type) =>
        axios.post(`${API_URL}/assets/${type}`, assetData).then((response) => response)

    createPaymentMethod = (paymentData) =>
        axios.post(`${API_URL}/payment-data`, paymentData).then((response) => response)

    emitPolicy = (emitData) =>
        axios.post(`${API_URL}/policies`, emitData).then((response) => response)

    checkPolicyStatus = (emissionId) =>
        axios
            .get(`${API_URL}/policies/status/${emissionId}`)
            .then(({ data: { data: { data, emissionStatus } = {} } }) => {
                switch (emissionStatus) {
                    case 'in_emission':
                        return {
                            status: EMIT_STATUS_WAITING,
                            data: null,
                        }

                    case 'requote':
                        return {
                            status: EMIT_STATUS_NEEDS_REQUOTE,
                            data: data.multiquoteId,
                        }

                    case 'ended':
                        return {
                            status: EMIT_STATUS_SUCCESS,
                            data: PolicyEmitted.fromJson(data),
                        }

                    case 'confirmation':
                        return {
                            status: EMIT_STATUS_NEEDS_CONFIRMATION,
                            data: {
                                oldQuote: VehicleCoverage.fromJson(data.oldQuote),
                                newQuote: VehicleCoverage.fromJson(data.newQuote),
                            },
                        }

                    case 'error':
                        throw new Error(data)

                    default:
                        throw new Error('unknown status')
                }
            })

    listCbuAccountTypes = () =>
        axios.get(`${API_URL}/insurances/account-types`).then(({ data: { data } }) => {
            return data.map((accountType) => CbuAccountType.fromJson(accountType))
        })

    listFinancialInstitutions = (insuranceCompany) =>
        axios
            .get(`${API_URL}/insurances/${insuranceCompany}/financial-institution`)
            .then(({ data: { data } }) => {
                return data.map((bank) => PolicyFinancialInstitution.fromJson(bank))
            })

    listBanks = (insuranceCompany) =>
        axios
            .get(`${API_URL}/insurances/${insuranceCompany}/bank-institution`)
            .then(({ data: { data } }) => {
                return data.map((bank) => PolicyBank.fromJson(bank))
            })

    showPolicy = (id) =>
        axios.get(`${API_URL}/policies/${id}`).then(({ data: { data } }) => {
            return PolicyEntity.fromJson(data)
        })

    deletePolicy = (id) =>
        axios.delete(`${API_URL}/policy/delete/${id}`).then(({ data: { success } }) => success)

    updatePolicy = (policyId, policyData) =>
        axios
            .put(`${API_URL}/policies/manual/${policyId}`, policyData)
            .then((response) => response)

    listPolicies = (params) =>
        axios
            .get(`${API_URL}/policies`, { params })
            .then(({ data: { data, pagination } }) => {
                return {
                    policies: data.map((policy) => {
                        return PoliciesEntity.fromJson(policy)
                    }),
                    paginator: Paginator.fromJson(pagination),
                }
            })

    listPoliciesByClient = (customerId, page) =>
        axios
            .get(`${API_URL}/policies`, { params: { customer: customerId, page } })
            .then(({ data: { data, pagination } }) => {
                return {
                    policies: data.map((policy) => {
                        return PoliciesEntity.fromJson(policy)
                    }),
                    paginator: Paginator.fromJson(pagination),
                }
            })

    updatePolicyCustomer = (customerId, policyData) =>
        axios
            .post(`${API_URL}/emit/customer/${customerId}/edit`, policyData)
            .then((response) => response)

    listPolicyAmendments = (policyId, params) =>
        axios
            .get(`${API_URL}/policies/${policyId}/amendments`, { params })
            // .then(({ data: { data, pagination } }) => ({
            .then(({ data: { data } }) => ({
                policyAmendments: data.map((policyAmendment) =>
                    PolicyAmendment.fromJson(policyAmendment)
                ),
                // paginator: Paginator.fromJson(pagination),
            }))

    getPolicyAmendmentDetails = (policyId, amendmentId) =>
        axios
            .get(`${API_URL}/policies/${policyId}/amendments/${amendmentId}`)
            .then(({ data: { data } }) => PolicyAmendmentDetail.fromJson(data))

    listPolicyAmendmentPetitions = (policyId, params) =>
        axios
            .get(`${API_URL}/policies/${policyId}/amendment-petitions`, { params })
            .then(({ data: { data, pagination } }) => ({
                policyAmendmentPetitions: data.map((policyAmendmentPetitions) =>
                    PolicyAmendmentPetitions.fromJson(policyAmendmentPetitions)
                ),
                paginator: Paginator.fromJson(pagination),
            }))

    listPolicyAmendmentsCancellationReasons = () =>
        axios
            .get(`${API_URL}/amendment-petitions/cancellation-reasons`)
            .then(({ data: { data } }) => data)

    getPolicyAmendmentPetitionDetails = (policyId, petitionId) =>
        axios
            .get(`${API_URL}/policies/${policyId}/amendment-petitions/${petitionId}`)
            .then(({ data: { data } }) => PolicyAmendmentPetitionDetail.fromJson(data))

    createPolicyAmendmentPetition = (policyId, createData) =>
        axios.post(`${API_URL}/policies/${policyId}/amendment-petitions`, createData)

    createPublicPolicyAmendmentPetition = (policyId, createData) =>
        axios.post(`${API_URL}/policies/${policyId}/customer-amendment-petitions`, createData)

    createManualPolicy = (createData) =>
        axios.post(`${API_URL}/policies/manual`, createData)

    cancelPolicyAmendmentPetition = (policyId, petitionId) =>
        axios.post(`${API_URL}/policies/${policyId}/amendment-petitions/${petitionId}/cancel`)

    getPolicyFormHeaderOptions = () =>
        new Promise((resolve) => {
            resolve(policyStepsHeaderJson.map((step) => FormStepHeaderOption.fromJson(step)))
        })

    getPaymentMethods = () =>
        new Promise((resolve) => {
            resolve(CommonDataStore.getPaymentOptions().map((method) => method))
        })

    getPolicyAmendmentPetitionDocumentLink = (policyId, petitionId, documentId) =>
        axios
            .get(`${API_URL}/policies/${policyId}/amendment-petitions/${petitionId}/downloadFile/${documentId}`)
            .then(({ data: { data: { url } } }) => url)

    getInsuranceTypes = () => 
        axios
            .get(`${API_URL}/insurances`)
            .then(({ data: { data } }) => data)

    getInsuranceCompanies = () =>
        axios
            .get(`${API_URL}/insurance-companies`)
            .then(({ data: { data } }) => data)

    getMyInsuranceCompanies = () =>
        axios
            .get(`${API_URL}/brokers/insurance-companies`)
            .then(({ data: { data } }) => data)
    
    getMyRisks = () =>
        axios
            .get(`${API_URL}/brokers/risk-types`)
            .then(({ data: { data } }) => data)

    getMyBrokers = () => 
        axios
            .get(`${API_URL}/brokers/children`)
            .then(({ data: { data } }) => data)
    
    getOrigins = () => 
        axios
            .get(`${API_URL}/groups`)
            .then(({ data: { data } }) => data)

    createMultiQuoteFromPolicy = (policyId, localityId, commissionType) => {
        const params = {}
        if (localityId) params.locality = localityId
        if (commissionType) params.commissionType = commissionType
        return axios
            .post(`${API_URL}/policies/${policyId}/multi-quote`, params)
            .then(({ data }) => data)
    }

    getBrokerOwnerData = (clientId) =>
        axios
            .get(`${API_URL}/broker-owner?clientId=${clientId}`)
            .then(({ data: { data } }) => BrokerDto.fromJson(data))

    fromStorage() {
        return this
    }
}

export default PolicyService;
